import { useId, useRef, useState, type FormEvent } from 'react';
import { ReviewDialog } from '../../payroll-review/components/ReviewDialog';
import type { DuplicateAcknowledgementInput, DuplicateFlag } from '../types';
import { describeFlagDates, describeOtherRun, matchLabel } from './duplicateText';

interface Props {
  flags: DuplicateFlag[];
  busy: boolean;
  error: string | null;
  onConfirm: (acknowledgements: DuplicateAcknowledgementInput[]) => void;
  onCancel: () => void;
}

/** Send for Approval, when some employees are also in another run for the same period or pay day. Never a hard block (a correction or bonus run can
 * legitimately share a period), but a conscious choice: one reason is required, and when any other run is already Approved the preparer must say so
 * explicitly. The server re-checks all of this and records who, when, why and which run. */
export function DuplicateAckDialog({ flags, busy, error, onConfirm, onCancel }: Props) {
  const reasonId = useId();
  const reasonErrorId = useId();
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const [reason, setReason] = useState('');
  const [approvedAck, setApprovedAck] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const anyApproved = flags.some((f) => f.otherRunIsApproved);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) {
      setProblem('Enter a reason. For example: "Correction run for a missed bonus".');
      reasonRef.current?.focus();
      return;
    }
    if (anyApproved && !approvedAck) {
      setProblem('Confirm that you know the other run is already approved.');
      return;
    }
    setProblem(null);
    onConfirm(flags.map((f) => ({ employeeId: f.employeeId, otherRunId: f.otherRunId, reason: trimmed, acknowledgesApprovedRun: f.otherRunIsApproved && approvedAck })));
  }

  return (
    <ReviewDialog
      title="Some employees are already in another run"
      busy={busy}
      onClose={onCancel}
      initialFocusRef={reasonRef}
      description={
        <>
          <p>
            These employees are also in another run for the same period or pay day. This can be right (for example a correction or a bonus run), so you can
            continue, but you need to say why. The reason is recorded and shown to the person who approves the run.
          </p>
          <ul>
            {flags.map((f) => (
              <li key={`${f.employeeId}-${f.otherRunId}`}>
                <strong>
                  {f.firstName} {f.lastName}
                </strong>{' '}
                — {describeOtherRun(f)}, {matchLabel(f.match)} ({describeFlagDates(f)}).
                {f.otherRunIsApproved && <strong> This run is already APPROVED.</strong>}
              </li>
            ))}
          </ul>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <label htmlFor={reasonId} className="payroll-review__label">
          Why is it right to pay these employees again? <span aria-hidden="true">*</span>
          <span className="payroll-review__sr-only"> (required)</span>
        </label>
        <textarea
          id={reasonId}
          ref={reasonRef}
          rows={3}
          maxLength={500}
          value={reason}
          required
          aria-required="true"
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? reasonErrorId : undefined}
          disabled={busy}
          onChange={(e) => {
            setReason(e.target.value);
            if (problem) setProblem(null);
          }}
        />
        {anyApproved && (
          <label className="cpr-ack">
            <input type="checkbox" checked={approvedAck} disabled={busy} onChange={(e) => setApprovedAck(e.target.checked)} />
            <span>I understand that at least one of these other runs is already approved, so these employees would be paid again.</span>
          </label>
        )}
        {problem && (
          <p id={reasonErrorId} className="error" role="alert">
            {problem}
          </p>
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div className="payroll-review__dialog-actions">
          <button type="button" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" disabled={busy}>
            {busy ? 'Sending…' : 'Confirm and send for approval'}
          </button>
        </div>
      </form>
    </ReviewDialog>
  );
}
